import React, { useEffect, useRef } from 'react';
import ExperimentNav from '../../components/ExperimentNav';
import { experiments } from '../../App';
import * as THREE from 'three';
import './VelocityCards.css';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PROMPT = 'Three.js로 3~5장 이미지 카드를 z축 깊이 달리 배치하고, wheel 이벤트의 deltaY를 velocity로 계산해 카드 회전 속도와 배경 hue를 실시간 연동해봐. 느리면 블루, 빠르면 오렌지. GSAP 대신 순수 JS로 먼저 프로토타입해봐.';

const CARD_IMAGES = [
  'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=600&h=400&fit=crop',
];

const VelocityCards: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    // --- Scene setup ---
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 6;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // --- Lighting ---
    const ambient = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambient);
    const directional = new THREE.DirectionalLight(0xffffff, 0.8);
    directional.position.set(2, 4, 6);
    scene.add(directional);

    // --- Load textures & create cards ---
    const loader = new THREE.TextureLoader();
    const cards: THREE.Mesh[] = [];
    const cardWidth = 2.4;
    const cardHeight = 1.6;
    const radius = 3.2;

    CARD_IMAGES.forEach((url, i) => {
      const texture = loader.load(url);
      texture.minFilter = THREE.LinearFilter;

      const geo = new THREE.PlaneGeometry(cardWidth, cardHeight);
      const mat = new THREE.MeshStandardMaterial({
        map: texture,
        side: THREE.DoubleSide,
        roughness: 0.3,
        metalness: 0.1,
      });
      const mesh = new THREE.Mesh(geo, mat);

      // Arrange in a circle at different z-depths
      const angle = (i / CARD_IMAGES.length) * Math.PI * 2;
      mesh.position.x = Math.sin(angle) * radius;
      mesh.position.z = Math.cos(angle) * radius - radius;
      mesh.position.y = (Math.random() - 0.5) * 0.6;

      mesh.userData = { baseAngle: angle, index: i };
      scene.add(mesh);
      cards.push(mesh);
    });

    // --- Velocity state ---
    let velocity = 0;
    let rotation = 0;
    let targetVelocity = 0;
    const friction = 0.95;
    const sensitivity = 0.0008;

    // --- Background hue ---
    // Blue (220) at rest → Orange (30) at max velocity
    const hueBlue = 220;
    const hueOrange = 30;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      targetVelocity += e.deltaY * sensitivity;
    };

    // Touch support
    let lastTouchY = 0;
    const onTouchStart = (e: TouchEvent) => {
      lastTouchY = e.touches[0].clientY;
    };
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const deltaY = lastTouchY - e.touches[0].clientY;
      lastTouchY = e.touches[0].clientY;
      targetVelocity += deltaY * sensitivity * 2;
    };

    container.addEventListener('wheel', onWheel, { passive: false });
    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchmove', onTouchMove, { passive: false });

    // --- HUD elements ---
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const hud = document.getElementById('velocity-hud');
    const velocityBar = document.getElementById('velocity-bar');
    const velocityValue = document.getElementById('velocity-value');
    const hueValue = document.getElementById('hue-value');

    // --- Animation loop ---
    let animId: number;

    const animate = () => {
      animId = requestAnimationFrame(animate);

      // Smooth velocity
      velocity += (targetVelocity - velocity) * 0.1;
      targetVelocity *= friction;
      velocity *= friction;

      rotation += velocity;

      // Map velocity to hue
      const speed = Math.min(Math.abs(velocity) * 50, 1); // 0..1 normalized
      const hue = hueBlue + (hueOrange - hueBlue + 360) % 360 * speed;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const finalHue = ((hue % 360) + 360) % 360;

      // Better hue interpolation: blue(220) → orange(30)
      // Going through red: 220 → 360/0 → 30
      let displayHue: number;
      if (speed < 0.5) {
        // Blue → Red
        displayHue = hueBlue + (360 - hueBlue) * (speed * 2);
      } else {
        // Red → Orange
        displayHue = (hueOrange) * ((speed - 0.5) * 2);
      }
      displayHue = ((displayHue % 360) + 360) % 360;

      // Apply background
      const saturation = 60 + speed * 20;
      const lightness = 8 + speed * 4;
      container.style.backgroundColor = `hsl(${displayHue}, ${saturation}%, ${lightness}%)`;

      // Update cards
      cards.forEach((card, i) => {
        const baseAngle = card.userData.baseAngle as number;
        const angle = baseAngle + rotation;

        card.position.x = Math.sin(angle) * radius;
        card.position.z = Math.cos(angle) * radius - radius;

        // Face camera
        card.lookAt(camera.position);

        // Scale based on z-depth (closer = bigger)
        const zNorm = (card.position.z + radius * 2) / (radius * 2);
        const scale = 0.6 + zNorm * 0.6;
        card.scale.set(scale, scale, scale);

        // Opacity based on z
        const mat = card.material as THREE.MeshStandardMaterial;
        mat.opacity = 0.4 + zNorm * 0.6;
        mat.transparent = true;

        // Tint cards with hue
        const cardHue = (displayHue + i * 20) % 360;
        mat.emissive.setHSL(cardHue / 360, 0.3 * speed, 0.05 * speed);
      });

      // Update HUD
      if (velocityBar) {
        velocityBar.style.width = `${speed * 100}%`;
        velocityBar.style.backgroundColor = `hsl(${displayHue}, 80%, 50%)`;
      }
      if (velocityValue) velocityValue.textContent = speed.toFixed(3);
      if (hueValue) hueValue.textContent = `${Math.round(displayHue)}°`;

      renderer.render(scene, camera);
    };

    animate();

    // --- Resize ---
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(animId);
      container.removeEventListener('wheel', onWheel);
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
      scene.clear();
    };
  }, []);

  const exp = experiments.find(e => e.id === 'velocity-cards');

  return (
    <div className="velocityCards" ref={containerRef}>
      <canvas ref={canvasRef} className="velocityCanvas" />

      <ExperimentNav prompt={exp?.prompt} title={exp?.title} />

      {/* HUD Overlay */}
      <div className="velocityHud" id="velocity-hud">
        <div className="hudRow">
          <span className="hudLabel">velocity</span>
          <div className="hudBarTrack">
            <div className="hudBar" id="velocity-bar" />
          </div>
          <span className="hudValue" id="velocity-value">0.000</span>
        </div>
        <div className="hudRow">
          <span className="hudLabel">hue</span>
          <span className="hudValue" id="hue-value">220°</span>
        </div>
        <div className="hudHint">scroll to spin</div>
      </div>
    </div>
  );
};

export default VelocityCards;
